"use client";

import { Core } from "@faire/design-tokens";
import { getCurrencySymbol } from "@faire/web/common/money";
import { enumValues } from "@faire/web/common/typescriptUtils";
import { Typography } from "@faire/web/slate/Typography";
import { CurrencyUnit } from "@faire/web-api/faire/currency/CurrencyUnit";
import { IFaireMoney } from "@faire/web-api/indigofair/data/IFaireMoney";
import * as React from "react";
import styled from "styled-components";

import { IOption } from "../Dropdown";
import { IMeasurementInputProps, MeasurementInput } from "../MeasurementInput";

const CurrencyTypography = styled(Typography)`
  position: relative;
  right: 8px;
`;

export interface MoneyInputProps
  extends Omit<
    IMeasurementInputProps<string>,
    "prefixNode" | "suffixNode" | "precision" | "ref"
  > {
  label?: string;

  currency?: string;
  /**
   * Handler for currency picker. When set, provides a currency
   * picker dropdown in the input.
   */
  onCurrencyChange?: (currency: CurrencyUnit) => void;
  prefix?: string;
  suffix?: string;

  excludeCents?: boolean;
}

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/component/input/web/money-input)
 */
export const MoneyInput: React.FC<MoneyInputProps> = (props) => {
  let prefix;
  if (props.prefix) {
    prefix = props.prefix;
  } else if (props.currency) {
    prefix = getCurrencySymbol(
      IFaireMoney.build({
        amount_cents: 0,
        currency: props.currency,
      })
    );
  }

  const handleOnChange = (value: number | undefined) => {
    props.onChange(value !== undefined ? Math.round(value * 100) : undefined);
  };

  const handleCurrencySelect = (option: IOption) => {
    props.onCurrencyChange?.(option.value as CurrencyUnit);
  };

  const currencyOptions: IOption[] = enumValues(CurrencyUnit)
    .filter((c) => c !== CurrencyUnit.CURRENCY_UNIT_UNKNOWN)
    .map(
      (c): IOption => ({
        value: c,
        label: c,
      })
    );

  const {
    value,
    currency,
    suffix,
    excludeCents,
    onChange: _, // Destructure onChange and onCurrencyChange to prevent them from going in "...rest" and being passed on to the MeasurementInput
    onCurrencyChange: __,
    ...rest
  } = props;

  const textColor = props.disabled ? Core.text.disabled : Core.text.default;

  return (
    <MeasurementInput
      precision={excludeCents ? 0 : 2}
      value={value !== undefined ? value / 100 : undefined}
      prefixNode={
        prefix ? (
          <CurrencyTypography color={textColor}>{prefix}</CurrencyTypography>
        ) : undefined
      }
      suffixNode={suffix}
      onChange={handleOnChange}
      unitInformation={
        props.onCurrencyChange
          ? {
              onUnitChange: handleCurrencySelect,
              unitOptions: currencyOptions,
              unitValue: props.currency,
            }
          : undefined
      }
      {...rest}
    />
  );
};
